import { type JSX, useEffect, useRef, useState } from 'react';

import {
  OrderedPlayer,
  type ServerEvent,
  type VoiceChoice,
  base64ToBytes,
  deviceId,
  languageChoices,
  listVoices,
  openVoiceSession,
  toPcm16k,
  voicePreviewUrl,
} from './voice-client';

/**
 * PENNY realtime voice — the minimum beta surface that proves the realtime core: start, listening /
 * speaking state, transcript, stop, handoff notice, microphone permission. Speaking over PENNY interrupts
 * her (the server hears it and stops her); there is also a Stop button.
 */
const HANDOFF_TEXT: Readonly<Record<string, string>> = {
  CUSTOMER_REQUEST: 'You asked for a person.',
  AI_UNCERTAIN: 'PENNY could not answer that reliably.',
  AUTHENTICATION_REQUIRED: 'This needs you to sign in.',
  CLINICAL_BOUNDARY: 'This needs a pharmacist.',
  TOOL_FAILURE: 'PENNY could not look that up.',
  POLICY_REQUIREMENT: 'This needs a person, not PENNY.',
};

const STATE_TEXT: Readonly<Record<string, string>> = {
  IDLE: 'Connecting…',
  LISTENING: 'Listening',
  TRANSCRIBING: 'Hearing you…',
  PROCESSING: 'Thinking…',
  TOOL_EXECUTION: 'Looking that up…',
  SPEAKING: 'PENNY is speaking',
  INTERRUPTED: 'Stopped',
  HANDOFF: 'Listening (a person is needed)',
  ENDED: 'Ended',
};

interface Line {
  readonly who: 'you' | 'penny' | 'system';
  readonly text: string;
}

const SESSION_KEY = 'jt.penny.voice.session';
/** Mic level (dBFS) that counts as the customer starting to talk over PENNY — for the local timing only. */
const ONSET_DB = -38;

function remembered(): { conversationId: string; voiceSessionId: string } | undefined {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw === null ? undefined : (JSON.parse(raw) as { conversationId: string; voiceSessionId: string });
  } catch {
    return undefined;
  }
}

function remember(value: { conversationId: string; voiceSessionId: string } | undefined): void {
  try {
    if (value === undefined) sessionStorage.removeItem(SESSION_KEY);
    else sessionStorage.setItem(SESSION_KEY, JSON.stringify(value));
  } catch {
    // Not kept: a reload then starts a new session.
  }
}

export function PennyVoicePage(): JSX.Element {
  const [state, setState] = useState<string>('OFF');
  const [lines, setLines] = useState<readonly Line[]>([]);
  const [handoff, setHandoff] = useState<string | undefined>();
  const [problem, setProblem] = useState<string | undefined>();
  const [timing, setTiming] = useState<string>('');
  const [voices, setVoices] = useState<readonly VoiceChoice[]>([]);
  const [voice, setVoice] = useState<string>('default');
  // Any language by default: each thing the customer says is detected, so they can switch at any time.
  const [language, setLanguage] = useState<string>('auto');
  const languages = languageChoices();

  useEffect(() => {
    void listVoices().then((v) => {
      setVoices(v);
      if (v.some((x) => x.key === 'uk-female-calm')) setVoice('uk-female-calm');
    });
  }, []);
  const live = useRef<{ stop: () => void } | undefined>(undefined);

  const secure = typeof window !== 'undefined' && window.isSecureContext === true;
  const micAvailable = secure && typeof navigator !== 'undefined' && navigator.mediaDevices !== undefined;

  useEffect(() => () => live.current?.stop(), []);

  async function start(): Promise<void> {
    setProblem(undefined);
    setHandoff(undefined);
    setState('IDLE');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
    } catch {
      setProblem('PENNY needs your microphone. Please allow it and try again.');
      setState('OFF');
      return;
    }
    const ctx = new AudioContext();
    let session: Awaited<ReturnType<typeof openVoiceSession>>;
    const device = deviceId();
    const earlier = remembered();
    try {
      // Reconnect to the same voice session (and conversation) when this tab had one; else start fresh.
      session =
        earlier !== undefined
          ? await openVoiceSession({ device, ...earlier }).catch(() =>
              openVoiceSession({ device, voice, language }),
            )
          : await openVoiceSession({ device, voice, language });
    } catch (error) {
      stream.getTracks().forEach((t) => t.stop());
      await ctx.close();
      setProblem(error instanceof Error ? error.message : 'PENNY voice is not available.');
      setState('OFF');
      return;
    }
    const { socket } = session;
    remember({ conversationId: session.conversationId, voiceSessionId: session.voiceSessionId });
    let serverState = 'IDLE';
    let onsetAt: number | undefined;
    const player = new OrderedPlayer(ctx, (generation) => {
      if (socket.readyState === WebSocket.OPEN)
        socket.send(JSON.stringify({ type: 'playback.ended', generation }));
    });
    const source = ctx.createMediaStreamSource(stream);
    // ScriptProcessor: universally supported and needs no separate worklet file.
    const processor = ctx.createScriptProcessor(4096, 1, 1);
    const mute = ctx.createGain();
    mute.gain.value = 0;
    processor.onaudioprocess = (e) => {
      if (socket.readyState !== WebSocket.OPEN) return;
      const samples = e.inputBuffer.getChannelData(0);
      // Local timing only (the server decides interruptions): when did the customer start talking over PENNY?
      if (onsetAt === undefined && serverState === 'SPEAKING' && player.playing) {
        let sum = 0;
        for (const v of samples) sum += v * v;
        const db = 10 * Math.log10(sum / samples.length + 1e-12);
        if (db > ONSET_DB) onsetAt = performance.now() - (samples.length / ctx.sampleRate) * 1000;
      }
      socket.send(toPcm16k(samples, ctx.sampleRate));
    };
    source.connect(processor);
    processor.connect(mute);
    mute.connect(ctx.destination);

    let ending = false;
    const stop = () => {
      ending = true;
      processor.disconnect();
      source.disconnect();
      stream.getTracks().forEach((t) => t.stop());
      if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'end' }));
      socket.close();
      void ctx.close();
      remember(undefined);
      live.current = undefined;
    };
    live.current = { stop };

    socket.onmessage = (m) => {
      const e = JSON.parse(String(m.data)) as ServerEvent;
      switch (e.type) {
        case 'session.ready':
          if (e['reconnected'] === true)
            setLines((l) => [...l, { who: 'system', text: 'Reconnected to the same conversation.' }]);
          break;
        case 'state':
          serverState = String(e['state']);
          if (serverState !== 'SPEAKING' && serverState !== 'INTERRUPTED') onsetAt = undefined;
          setState(serverState);
          break;
        case 'metrics': {
          const ms = (e['ms'] ?? {}) as Record<string, number>;
          const parts = [
            ms['speechEndToTranscript'] !== undefined
              ? `speech end → transcript ${ms['speechEndToTranscript']} ms`
              : '',
            ms['speechEndToFirstAudio'] !== undefined
              ? `speech end → first audio ${ms['speechEndToFirstAudio']} ms`
              : '',
            ms['interruptToCancel'] !== undefined
              ? `interrupt → stop sent ${ms['interruptToCancel']} ms`
              : '',
          ].filter((x) => x !== '');
          if (parts.length > 0) setTiming(`Turn ${String(e['generation'])}: ${parts.join(' · ')}`);
          break;
        }
        case 'transcript.final':
          if (String(e['text']) !== '') setLines((l) => [...l, { who: 'you', text: String(e['text']) }]);
          break;
        case 'response':
          setLines((l) => [...l, { who: 'penny', text: String(e['text']) }]);
          break;
        case 'audio.part':
          player.part(Number(e['generation']), base64ToBytes(String(e['audio'])), String(e['mediaType']));
          break;
        case 'audio.end':
          player.end(Number(e['generation']));
          break;
        case 'playback.stop': {
          const received = performance.now();
          const generation = Number(e['generation']);
          const cut = player.stop(generation);
          const stopped = performance.now();
          const report = {
            type: 'client.metrics',
            generation,
            sourcesStopped: cut,
            stopReceivedToStoppedMs: Math.round(stopped - received),
            ...(onsetAt !== undefined
              ? {
                  onsetToStopReceivedMs: Math.round(received - onsetAt),
                  onsetToStoppedMs: Math.round(stopped - onsetAt),
                }
              : {}),
            staleParts: player.staleParts,
          };
          onsetAt = undefined;
          if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(report));
          setTiming(
            `Interrupted turn ${generation}: browser audio stopped ${report.stopReceivedToStoppedMs} ms after the stop arrived` +
              (report.onsetToStoppedMs !== undefined
                ? ` (${report.onsetToStoppedMs} ms after you started talking)`
                : ''),
          );
          break;
        }
        case 'handoff.required':
          setHandoff(HANDOFF_TEXT[String(e['reason'])] ?? 'A person is needed.');
          break;
        case 'error':
          if (e['code'] === 'TTS_UNAVAILABLE')
            setProblem('PENNY’s voice is unavailable — her reply is shown above.');
          if (e['code'] === 'STT_UNAVAILABLE')
            setProblem('PENNY could not hear that. Please try again, or type instead.');
          break;
        case 'session.ended':
          setState('ENDED');
          stop();
          break;
        default:
          break;
      }
    };
    socket.onclose = () => {
      if (ending) {
        setState((s) => (s === 'ENDED' ? s : 'OFF'));
        return;
      }
      // Dropped (network, server restart): release this socket's audio; the next Start reconnects to the
      // same voice session while the server still holds it.
      processor.disconnect();
      source.disconnect();
      stream.getTracks().forEach((t) => t.stop());
      void ctx.close();
      live.current = undefined;
      setLines((l) => [...l, { who: 'system', text: 'Disconnected — press Start to reconnect.' }]);
      setState('OFF');
    };
  }

  return (
    <main id="main" className="jt-container" style={{ padding: '32px 0', maxWidth: 720 }}>
      <h1>Talk to PENNY</h1>
      <p>Beta: speak naturally. You can talk over PENNY to interrupt her.</p>
      <p style={{ fontSize: 14 }}>
        PENNY adapts her tone to how you sound (for example calmer if you sound stressed). This is used only
        for her next reply — it is never stored.
      </p>
      {state === 'OFF' || state === 'ENDED' ? (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0' }}>
          <label>
            Voice{' '}
            <select value={voice} onChange={(e) => setVoice(e.target.value)}>
              {voices.map((v) => (
                <option key={v.key} value={v.key}>
                  {v.label}
                </option>
              ))}
            </select>
          </label>
          <button type="button" onClick={() => void new Audio(voicePreviewUrl(voice)).play()}>
            Preview voice
          </button>
          <label>
            Language{' '}
            <select value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option value="auto">Any language (switch at any time)</option>
              <option value="en">English only</option>
              {languages
                .filter((l) => l.code !== 'en')
                .map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.name}
                  </option>
                ))}
            </select>
          </label>
        </div>
      ) : null}
      {language !== 'en' ? (
        <p role="note" style={{ border: '1px solid currentColor', padding: 12 }}>
          Other languages are a beta test. PENNY’s emergency and pharmacist-referral checks currently
          understand English only — in an emergency call 999, or 111 if it is not life-threatening.
        </p>
      ) : null}
      {!micAvailable ? (
        <p role="alert">
          Your browser only allows the microphone on a secure (https) page. PENNY voice will work here once
          this site is served over https.
        </p>
      ) : state === 'OFF' || state === 'ENDED' ? (
        <button type="button" onClick={() => void start()}>
          Start conversation
        </button>
      ) : (
        <button type="button" onClick={() => live.current?.stop()}>
          End conversation
        </button>
      )}
      <p aria-live="polite">
        <strong>{STATE_TEXT[state] ?? (state === 'OFF' ? 'Not started' : state)}</strong>
      </p>
      {handoff !== undefined ? (
        <p role="status" style={{ border: '1px solid currentColor', padding: 12 }}>
          A member of our team is needed. {handoff}
        </p>
      ) : null}
      {problem !== undefined ? <p role="alert">{problem}</p> : null}
      {timing !== '' ? <p style={{ fontSize: 14, opacity: 0.8 }}>{timing}</p> : null}
      <ol style={{ listStyle: 'none', padding: 0 }}>
        {lines.map((l, i) => (
          <li key={i} style={{ margin: '12px 0', whiteSpace: 'pre-wrap' }}>
            {l.who === 'system' ? <em>{l.text}</em> : null}
            {l.who === 'system' ? null : (
              <>
                <strong>{l.who === 'you' ? 'You' : 'PENNY'}:</strong> {l.text}
              </>
            )}
          </li>
        ))}
      </ol>
    </main>
  );
}
